import { getCsrfTokenApi, logoutApi, refreshTokenApi } from '~/api/auth';
import { useCookieOptions } from '~/composables/useCookieOptions';
import { useDeviceId } from '~/composables/useDeviceId';
import { useResetStore } from '~/composables/useResetStore';
import { STORAGE_KEYS } from '~/constants/storageKeys';
import { useAuthStore } from '~/stores/auth';
import { useProfileStore } from '~/stores/profile';
import { useRoleStore } from '~/stores/role';
import { navigateTo } from '#app';

interface AuthHeaders {
  [key: string]: string;
  TOKEN: string;
}

export const useAuth = () => {
  const authStore = useAuthStore();
  const localePath = useLocalePath();

  const getAuthHeaders = (): AuthHeaders => {
    return {
      TOKEN: authStore.getToken() || '',
    };
  };

  /**
   * 靜默刷新 token（用於 API 請求中的自動刷新）
   * 只更新 token，不觸發登入流程的重定向
   * @returns {Promise<boolean>} 刷新是否成功
   */
  const refreshTokenSilently = async (): Promise<boolean> => {
    if (!authStore.getRefreshToken()) {
      return false;
    }

    try {
      const data = await refreshTokenApi(authStore.getRefreshToken());
      // 只更新 token，不觸發登入流程
      authStore.setTokens(data.token, data.refresh_token);
      const csrfSuccess = await refreshCsrfTokenSilently();
      if (!csrfSuccess) {
        console.warn('CSRF token refresh failed after silent token refresh');
      }
      return true;
    } catch (error) {
      console.warn('Silent token refresh failed:', error);
      return false;
    }
  };

  const refreshCsrfTokenSilently = async (): Promise<boolean> => {
    try {
      const csrfToken = await getCsrfTokenApi();
      if (!csrfToken) {
        return false;
      }
      authStore.setCsrfToken(csrfToken);
      return true;
    } catch (error) {
      console.warn('Silent CSRF token refresh failed:', error);
      return false;
    }
  };

  const refreshTokenOnUnauthorized = async () => {
    if (!authStore.refreshToken) {
      // 沒有 refreshToken，直接清除本地資料並跳轉（跳過 API 調用，避免無限循環）
      await logout(undefined, true);
      window.location.href = localePath('/login');
      // await navigateTo(localePath('/login'),{replace: true});
      return;
    }

    try {
      const data = await refreshTokenApi(authStore.getRefreshToken());
      await login(data.token, data.refresh_token);
    } catch (error) {
      // refreshToken 也失效，清除本地資料並跳轉（跳過 API 調用，避免無限循環）
      await logout(undefined, true);
      window.location.href = localePath('/login');
      // await navigateTo(localePath('/login'),{replace: true});
    }
  };

  const refreshTokenAfterOrgSwitch = async () => {
    if (!authStore.refreshToken) {
      console.warn('No refresh token available for organization switch');
      return false;
    }

    try {
      const data = await refreshTokenApi(authStore.getRefreshToken());
      authStore.clearTokens();
      await nextTick();
      authStore.setTokens(data.token, data.refresh_token);
      const csrfSuccess = await refreshCsrfTokenSilently();
      if (!csrfSuccess) {
        console.warn('CSRF token refresh failed after organization switch');
      }
      return true;
    } catch (error) {
      console.warn('Failed to refresh token after organization switch:', error);
      return false;
    }
  };

  const getRedirectUrl = (): { path: string; query: Record<string, string> } | null => {
    // 檢查是否有需要重定向的 URL（優先 cookie，再 localStorage）
    let redirectDataObj: { path: string; query: Record<string, string> } | null = null;

    if (!import.meta.client) {
      return null;
    }

    // 先嘗試讀 cookie
    try {
      // 使用統一的 cookie 選項，確保 domain 設定正確
      const redirectCookie = useCookie(
        STORAGE_KEYS.REDIRECT_AFTER_LOGIN,
        useCookieOptions<string>()
      );
      const cookieVal = redirectCookie.value;
      if (cookieVal) {
        if (typeof cookieVal === 'string') {
          try {
            redirectDataObj = JSON.parse(cookieVal);
          } catch {
            // 如果不是有效 JSON，忽略，改用 localStorage
          }
        } else if (typeof cookieVal === 'object') {
          redirectDataObj = cookieVal;
        }
      }
    } catch {
      // ignore
    }

    // 如果 cookie 沒有或解析失敗，再用 localStorage（字串）
    if (!redirectDataObj) {
      const redirectJson = localStorage.getItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
      if (redirectJson) {
        try {
          redirectDataObj = JSON.parse(redirectJson);
        } catch {
          // ignore
        }
      }
    }

    return redirectDataObj;
  };

  const handleRedirectAfterLogin = async (): Promise<void> => {
    const redirectDataObj = getRedirectUrl();

    if (redirectDataObj?.path) {
      const { path, query } = redirectDataObj;
      // 使用統一的 cookie 選項，確保 domain 設定正確
      useCookie(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, useCookieOptions<string>()).value = '';
      localStorage.removeItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
      await navigateTo({ path, query });
      return;
    }

    // 如果沒有重定向 URL，則重定向到 dashboard
    await navigateTo(localePath('/dashboard'));
  };

  const login = async (_token: string, _refreshToken: string | null, redirect_uri?: string) => {
    // 如果沒有提供 refresh_token，嘗試從 store 中取得
    const finalRefreshToken = _refreshToken || authStore.getRefreshToken();

    authStore.setTokens(_token, finalRefreshToken);
    await nextTick();
    const csrfSuccess = await refreshCsrfTokenSilently();
    if (!csrfSuccess) {
      console.warn('CSRF token refresh failed after login');
    }

    // 登入成功後自動載入權限資料
    try {
      const roleStore = useRoleStore();
      await Promise.all([
        roleStore.getMemberRules(),
        // roleStore.getApiGroups()
        roleStore.getAllUserAuthApis(),
      ]);
    } catch (error) {
      console.warn('load permission data failed:', error);
      // 不阻擋登入流程，讓組件可以fallback到原本的載入邏輯
    }

    if (redirect_uri) {
      // 使用統一的 cookie 選項，確保 domain 設定正確
      useCookie(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, useCookieOptions<string>()).value = '';
      localStorage.removeItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
      await navigateTo(redirect_uri);
      return;
    }

    // 處理登入後的重定向邏輯
    await handleRedirectAfterLogin();
  };

  /**
   * 登出功能
   * @param deviceId 可選的設備 ID，如果提供則只登出該設備，否則登出所有設備
   * @param skipApi 是否跳過 API 調用（用於 token 失效時避免無限循環）
   */
  const logout = async (deviceId?: string, skipApi = false) => {
    // 如果沒有提供 deviceId，嘗試從 useDeviceId 獲取
    let finalDeviceId = deviceId;
    if (!finalDeviceId && import.meta.client) {
      try {
        const { getOrCreateDeviceId } = useDeviceId();
        finalDeviceId = getOrCreateDeviceId();
      } catch (error) {
        console.warn('Failed to get device ID for logout:', error);
      }
    }

    // 只有在不跳過 API 時才呼叫登出 API
    // 當 token 失效時，跳過 API 調用避免無限循環（因為 logoutApi 需要認證）
    if (!skipApi) {
      try {
        await logoutApi(finalDeviceId);
      } catch (error) {
        // 登出 API 失敗時記錄錯誤，但不阻擋本地清除流程
        // 這樣即使 API 失敗，用戶也能正常登出
        console.warn('Logout API call failed, but continuing with local cleanup:', error);
      }
    } else {
      console.log('Skipping logout API call due to token expiration');
    }

    // 使用 useResetStore 重置所有 stores
    const resetStore = useResetStore();
    resetStore.all();

    // 清除 cookies（因為 pinia reset 不會清除 cookies）
    authStore.clearTokens();
  };

  /**
   * 處理 Google 社交登入
   * 在導向 Google 授權頁面之前，先清除前端之前設定的 cookies
   */
  const handleSocialLogin = (invite_code?: string) => {
    localStorage.setItem('from_url', window.location.href);
    const config = useRuntimeConfig();
    const clientId = config.public.GOOGLE_CLIENT_ID as string;
    const redirectUri = config.public.GOOGLE_REDIRECT_URI as string;

    // 在導向 Google 授權頁面之前，先清除前端之前設定的 cookies
    if (import.meta.client) {
      authStore.clearTokens();
    }

    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

    // 統一導向 callback 頁面，將 invite_code 等參數帶在 state 中
    let stateUrl = `${window.location.origin}/auth/callback`;
    if (invite_code) {
      stateUrl += `?invite_code=${encodeURIComponent(invite_code)}`;
    }

    const params = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'email profile',
      access_type: 'offline',
      prompt: 'consent',
      state: stateUrl,
    };

    for (const [key, value] of Object.entries(params)) {
      authUrl.searchParams.append(key, value);
    }

    window.location.href = authUrl.toString();
  };

  return {
    login,
    logout,
    refreshTokenOnUnauthorized,
    refreshTokenSilently,
    refreshCsrfTokenSilently,
    refreshTokenAfterOrgSwitch,
    getAuthHeaders,
    handleSocialLogin,
    getRedirectUrl,
    handleRedirectAfterLogin,
  };
};
